import type { PercentCrop, PixelCrop } from 'react-image-crop';

export const STICKER_FAIRY_DEFAULT_CROP: PercentCrop = {
  unit: '%',
  width: 90,
  height: 90,
  x: 5,
  y: 5,
};

const MIN_CROP_PERCENT = 12;

const clamp = (value: number, min: number, max: number) => {
  return Math.min(max, Math.max(min, value));
};

export const normalizePercentCrop = (crop: PercentCrop): PercentCrop => {
  const width = clamp(crop.width || MIN_CROP_PERCENT, MIN_CROP_PERCENT, 100);
  const height = clamp(crop.height || MIN_CROP_PERCENT, MIN_CROP_PERCENT, 100);

  return {
    unit: '%',
    width,
    height,
    x: clamp(crop.x || 0, 0, 100 - width),
    y: clamp(crop.y || 0, 0, 100 - height),
  };
};

export const movePercentCrop = (
  crop: PercentCrop | undefined,
  deltaX: number,
  deltaY: number
): PercentCrop => {
  const current = normalizePercentCrop(crop || STICKER_FAIRY_DEFAULT_CROP);

  return normalizePercentCrop({
    ...current,
    x: current.x + deltaX,
    y: current.y + deltaY,
  });
};

export const resizePercentCrop = (
  crop: PercentCrop | undefined,
  delta: number
): PercentCrop => {
  const current = normalizePercentCrop(crop || STICKER_FAIRY_DEFAULT_CROP);
  const centerX = current.x + current.width / 2;
  const centerY = current.y + current.height / 2;
  const width = clamp(current.width + delta, MIN_CROP_PERCENT, 100);
  const height = clamp(current.height + delta, MIN_CROP_PERCENT, 100);

  return normalizePercentCrop({
    unit: '%',
    width,
    height,
    x: centerX - width / 2,
    y: centerY - height / 2,
  });
};

export const percentCropToPixelCrop = (
  crop: PercentCrop,
  imageWidth: number,
  imageHeight: number
): PixelCrop | null => {
  const normalized = normalizePercentCrop(crop);

  if (imageWidth <= 0 || imageHeight <= 0) {
    return null;
  }

  return {
    unit: 'px',
    x: Math.round((normalized.x / 100) * imageWidth),
    y: Math.round((normalized.y / 100) * imageHeight),
    width: Math.max(1, Math.round((normalized.width / 100) * imageWidth)),
    height: Math.max(1, Math.round((normalized.height / 100) * imageHeight)),
  };
};
